import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, getDoc, doc } from 'firebase/firestore';
import { db, auth } from '../utils/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { geminiAI } from '../utils/geminiAI';
import Header from '../components/Header';

const SupportTicket = () => {
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();
  const [ticket, setTicket] = useState({
    title: '',
    description: '',
    category: '',
    priority: 'medium',
    contactEmail: '',
    contactPhone: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    if (user && !loading) {
      setTicket(prev => ({
        ...prev,
        contactEmail: user.email || '',
        userId: user.uid,
        userName: user.displayName || user.email?.split('@')[0] || 'User'
      }));
      
      // Check user role to determine where to navigate back to
      const checkUserRole = async () => {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            setUserRole(userDoc.data().role || 'user');
          }
        } catch (error) {
          console.error('Error checking user role:', error);
          setUserRole('user');
        }
      };
      checkUserRole();
    }
  }, [user, loading]);

  const getBackPath = () => {
    if (userRole === 'admin') {
      return '/admin';
    } else if (userRole === 'host') {
      return '/home';
    } else {
      return '/team/dashboard';
    }
  };

  const categories = [
    { value: 'account', label: 'Account Issues', icon: '👤' },
    { value: 'team', label: 'Team Management', icon: '🏆' },
    { value: 'game', label: 'Game Problems', icon: '🧠' },
    { value: 'venue', label: 'Venue Issues', icon: '📍' },
    { value: 'payment', label: 'Payment/Billing', icon: '💰' },
    { value: 'technical', label: 'Technical Problems', icon: '🔧' },
    { value: 'feature', label: 'Feature Request', icon: '💡' },
    { value: 'other', label: 'Other', icon: '📝' }
  ];

  const priorities = [
    { value: 'low', label: 'Low', color: 'text-green-600' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-600' },
    { value: 'high', label: 'High', color: 'text-orange-600' },
    { value: 'urgent', label: 'Urgent', color: 'text-red-600' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      // Auto-categorize if not selected
      let finalCategory = ticket.category;
      if (!finalCategory) {
        finalCategory = await autoCategorizeTicket(ticket.title, ticket.description);
      }

      // Auto-assign priority if not set
      let finalPriority = ticket.priority;
      if (finalPriority === 'medium') {
        finalPriority = await autoAssignPriority(ticket.title, ticket.description, finalCategory);
      }

      const ticketData = {
        ...ticket,
        category: finalCategory,
        priority: finalPriority,
        status: 'open',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        assignedTo: null,
        responses: [],
        aiAnalysis: {
          autoCategorized: !ticket.category,
          autoPrioritized: ticket.priority === 'medium',
          suggestedSolutions: await generateSuggestedSolutions(ticket.title, ticket.description, finalCategory),
          sentiment: (await geminiAI.analyzeTicket(ticket.title, ticket.description)).sentiment,
          keywords: (await geminiAI.analyzeTicket(ticket.title, ticket.description)).keywords,
          urgency: (await geminiAI.analyzeTicket(ticket.title, ticket.description)).urgency
        }
      };

      await addDoc(collection(db, 'supportTickets'), ticketData);
      setSuccess(true);
      
      // Reset form
      setTicket({
        title: '',
        description: '',
        category: '',
        priority: 'medium',
        contactEmail: ticket.contactEmail,
        contactPhone: ''
      });

    } catch (err) {
      console.error('Error submitting ticket:', err);
      setError('Failed to submit ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // AI-powered auto-categorization
  const autoCategorizeTicket = async (title, description) => {
    try {
      const analysis = await geminiAI.analyzeTicket(title, description);
      return analysis.category;
    } catch (error) {
      console.error('Auto-categorization error:', error);
      return 'other';
    }
  };

  // AI-powered priority assignment
  const autoAssignPriority = async (title, description, category) => {
    try {
      const analysis = await geminiAI.analyzeTicket(title, description);
      return analysis.priority;
    } catch (error) {
      console.error('Auto-priority error:', error);
      return 'medium';
    }
  };

  // AI-powered solution suggestions
  const generateSuggestedSolutions = async (title, description, category) => {
    try {
      const analysis = await geminiAI.analyzeTicket(title, description);
      return analysis.suggestedSolutions;
    } catch (error) {
      console.error('Solution generation error:', error);
      return ['Please contact support for assistance'];
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-400 mx-auto"></div>
          <p className="mt-4 text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="bg-gray-800 p-8 rounded-lg shadow-md max-w-md text-center border border-gray-700">
            <div className="text-6xl mb-4">✅</div>
            <h2 className="text-2xl font-bold text-white mb-4">Ticket Submitted!</h2>
            <p className="text-gray-300 mb-6">
              Your support ticket has been submitted successfully. Our team will review it and get back to you soon.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => setSuccess(false)}
                className="w-full bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 transition"
              >
                Submit Another Ticket
              </button>
              <button
                onClick={() => navigate(getBackPath())}
                className="w-full bg-gray-600 text-white py-2 px-4 rounded hover:bg-gray-700 transition"
              >
                Back to {userRole === 'admin' ? 'Admin Dashboard' : userRole === 'host' ? 'Home' : 'Team Dashboard'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(getBackPath())}
            className="flex items-center gap-2 text-green-400 hover:text-green-300 mb-4"
          >
            ← Back to {userRole === 'admin' ? 'Admin Dashboard' : userRole === 'host' ? 'Home' : 'Team Dashboard'}
          </button>
          <h1 className="text-3xl font-bold text-white mb-2">Submit Support Ticket</h1>
          <p className="text-gray-300">
            Need help? Submit a ticket and our AI-powered support system will assist you quickly.
          </p>
        </div>

        {/* Ticket Form */}
        <div className="bg-gray-800 rounded-lg shadow-md p-8 border border-gray-700">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Issue Title *
              </label>
              <input
                type="text"
                required
                value={ticket.title}
                onChange={(e) => setTicket({ ...ticket, title: e.target.value })}
                className="w-full p-3 border border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gray-700 text-white placeholder-gray-400"
                placeholder="Brief description of your issue"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Detailed Description *
              </label>
              <textarea
                required
                rows={6}
                value={ticket.description}
                onChange={(e) => setTicket({ ...ticket, description: e.target.value })}
                className="w-full p-3 border border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gray-700 text-white placeholder-gray-400"
                placeholder="Please provide as much detail as possible about your issue..."
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Category
              </label>
              <select
                value={ticket.category}
                onChange={(e) => setTicket({ ...ticket, category: e.target.value })}
                className="w-full p-3 border border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gray-700 text-white"
              >
                <option value="">Auto-detect (recommended)</option>
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.icon} {cat.label}
                  </option>
                ))}
              </select>
              <p className="text-sm text-gray-400 mt-1">
                Leave empty for AI-powered auto-categorization
              </p>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Priority
              </label>
              <select
                value={ticket.priority}
                onChange={(e) => setTicket({ ...ticket, priority: e.target.value })}
                className="w-full p-3 border border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gray-700 text-white"
              >
                {priorities.map(priority => (
                  <option key={priority.value} value={priority.value}>
                    {priority.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Contact Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Contact Email *
                </label>
                <input
                  type="email"
                  required
                  value={ticket.contactEmail}
                  onChange={(e) => setTicket({ ...ticket, contactEmail: e.target.value })}
                  className="w-full p-3 border border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gray-700 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Phone Number (optional)
                </label>
                <input
                  type="tel"
                  value={ticket.contactPhone}
                  onChange={(e) => setTicket({ ...ticket, contactPhone: e.target.value })}
                  className="w-full p-3 border border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gray-700 text-white placeholder-gray-400"
                  placeholder="For urgent issues"
                />
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-md hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {submitting ? 'Submitting...' : 'Submit Ticket'}
            </button>
          </form>
        </div>

        {/* AI Features Info */}
        <div className="mt-8 bg-blue-900 border border-blue-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-200 mb-3">🤖 AI-Powered Support Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-blue-300">
            <div>
              <div className="font-semibold">Auto-Categorization</div>
              <div>AI analyzes your issue and assigns the best category</div>
            </div>
            <div>
              <div className="font-semibold">Smart Prioritization</div>
              <div>Automatically determines urgency based on content</div>
            </div>
            <div>
              <div className="font-semibold">Instant Solutions</div>
              <div>AI suggests solutions while you wait for human support</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportTicket;
